const FOCUSABLE_SELECTORS = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
const TRANSITION_DURATION = 800; // keep in sync with --drawer-duration in base.css
const LINE_ERROR_DURATION = 3000;

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.previousActiveElement = null;
    this.isOpen = false;
    this.focusTrapHandler = null;
    this._checkoutPending = false;
    this._checkoutUrl = '';
    this._unsubscribe = null;
    // line key -> the quantity the shopper has clicked their way to, not yet sent. Collapses a
    // run of taps on one line into a single write; see _scheduleFlush.
    this._pendingQty = new Map();
    // Lines the cart brought down itself (_healOverStockLines), so the flush can say so.
    this._healedQty = new Map();
    // line key -> the clamp message that line is owed. Held as state rather than written into
    // the row and forgotten: every render replaces [data-cart-items], so a message living only
    // in the DOM is erased by the next render — a discount write, a resync, or the render
    // belonging to a later tap — with nothing to put it back. renderSection repaints from here.
    this._clampNotices = new Map();
    // line key -> the timer hiding that line's message. Keyed so a second message on one line
    // replaces the first one's deadline instead of inheriting it.
    this._lineErrorTimers = new Map();
    this._flushTimer = null;
    this._mutationChain = Promise.resolve();
    this._queuedMutations = 0;
    this._executingMutations = 0;
  }

  connectedCallback() {
    this.overlay = this.querySelector('[data-cart-overlay]');
    this.panel = this.querySelector('[data-cart-panel]');
    this._checkoutUrl = this.querySelector('[data-cart-checkout]')?.dataset.checkoutUrl ?? '';

    this.overlay?.addEventListener('click', () => this.close());

    // <cart-serviceability> resolves after its request lands, which is later than any
    // render, so checkout has to be re-judged when it says so rather than only at
    // render time.
    this.addEventListener('serviceability:changed', () => this._syncCheckoutAvailability());

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-close]')) this.close();
    });

    // Escape belongs to the topmost drawer. When the coupon drawer is stacked
    // over this one it handles its own Escape, and closing the bag underneath
    // it as well would dismiss both at once.
    this.handleKeydown = (event) => {
      if (event.key !== 'Escape' || !this.isOpen) return;
      if (document.querySelector('coupon-drawer')?.isOpen) return;
      this.close();
    };
    document.addEventListener('keydown', this.handleKeydown);

    this.handleCartTrigger = (event) => {
      if (event.target.closest('[data-cart-trigger]')) {
        event.preventDefault();
        this.open();
      }
    };
    document.addEventListener('click', this.handleCartTrigger);

    this.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-cart-checkout]');
      if (!btn) return;
      // Already disabled by _syncCheckoutAvailability when a line cannot be fulfilled; guarded
      // again so a stray programmatic click cannot get past it either.
      if (this._hasUnavailableLines()) return;
      if (CartStore.pendingOps > 0) {
        this._checkoutPending = true;
        const spinner = this.querySelector('[data-checkout-spinner]');
        const text = this.querySelector('[data-checkout-text]');
        btn.disabled = true;
        if (text) text.hidden = true;
        if (spinner) spinner.hidden = false;
      } else {
        window.location.href = this._checkoutUrl;
      }
    });

    // Same two guards for GoKwik's button: a bag holding an unfulfillable line, and a cart still
    // settling. Their button carries an inline onclick, so the event has to be caught in the
    // capture phase to stop it reaching the target, and both states live on classes of ours
    // because GoKwik's observer strips `disabled` and `.disabled` roughly every 700ms.
    this.addEventListener(
      'click',
      (event) => {
        if (!event.target.closest('.gokwik-checkout button')) return;

        // .is-cart-blocked already takes the pointer events; this covers the window between a
        // line going unavailable and the render that applies the class.
        if (this._hasUnavailableLines()) {
          event.preventDefault();
          event.stopPropagation();
          this._syncCheckoutAvailability();
          return;
        }

        if (CartStore.pendingOps === 0) return;
        event.preventDefault();
        event.stopPropagation();
        this._checkoutPending = true;
        this.querySelector('.gokwik-checkout')?.classList.add('is-cart-busy');
      },
      true,
    );

    this.addEventListener('click', (event) => this._handleQtyRemove(event));

    this.addEventListener('click', (event) => {
      const removeBtn = event.target.closest('[data-cart-remove]');
      if (!removeBtn) return;
      const row = removeBtn.closest('[data-item-key]');
      if (row) this._handleRemove(row.dataset.itemKey);
    });

    // Hidden rather than removed: the region is Liquid's, and every cart write re-renders it.
    this.addEventListener('click', (event) => {
      if (!event.target.closest('[data-cart-notice-dismiss]')) return;
      const noticeEl = event.target.closest('[data-cart-notice]');
      if (noticeEl) noticeEl.hidden = true;
    });

    // Applied-coupon chips in the bag ([data-cart-applied-coupons]).
    this.addEventListener('click', (event) => {
      const removeBtn = event.target.closest('[data-cart-coupon-remove]');
      if (!removeBtn) return;
      event.preventDefault();
      this.removeCoupon(removeBtn.dataset.code ?? '');
    });

    // A back/forward restore replays the cached DOM and JS heap: connectedCallback does not
    // run again, DOMContentLoaded does not fire, and CartStore.init() is memoised — so the
    // drawer keeps showing the cart as it stood when the page was cached. A line added since
    // reads as missing and one removed since lingers.
    this.handlePageShow = (event) => {
      if (event.persisted) this._resyncFromServer();
    };
    window.addEventListener('pageshow', this.handlePageShow);

    // Straight away, against the server-rendered rows: CartStore.init() is async, and a bag that
    // arrives holding a sold-out line must not offer checkout in the meantime.
    this._syncCheckoutAvailability();

    this._unsubscribe = CartStore.subscribe(this._onStoreChange.bind(this));
    CartStore.init();
  }

  disconnectedCallback() {
    this._unsubscribe?.();
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleCartTrigger);
    window.removeEventListener('pageshow', this.handlePageShow);
    this.removeTrapFocus();
  }

  // Pulls the cart and the drawer's own markup back in step with the server. The store update
  // refreshes the header counts through its subscriber; the section render replaces the line
  // items, which the store alone cannot do.
  async _resyncFromServer() {
    if (CartStore.pendingOps > 0) return;

    await CartStore.refresh();
    await this.renderFromServer();
  }

  // Redraws when the page turns out to be older than the cart it is showing.
  //
  // The line items are server-rendered, so they are only ever as current as the HTML they
  // arrived in. A back-navigation can replay HTML from before the last add while the scripts
  // start fresh and read the real cart: the header count updates from the store and the items
  // do not, leaving an empty bag under a "2". The pageshow handler above only covers the
  // bfcache case — `persisted` is false for an ordinary cached back-navigation, and nothing
  // else on that path re-renders the section.
  //
  // Comparing what Liquid rendered against what the cart says catches it however the stale
  // page arrived, and costs nothing when they agree, which is every normal page load.
  _reconcileStaleMarkup(itemCount) {
    const renderedCount = Number.parseInt(this.dataset.renderedCount ?? '', 10);
    if (Number.isNaN(renderedCount) || renderedCount === itemCount) return;

    // One attempt per count, so a fetch that keeps failing cannot retry on every notify.
    if (this._staleResyncFor === itemCount) return;
    this._staleResyncFor = itemCount;

    this.renderFromServer();
  }

  // Heal carts built before this stamp existed. GoKwik replaces our checkout
  // button at runtime, so opening the drawer is the last hook we own before
  // checkout. Skipped mid-mutation so it can't clobber optimistic state.
  // Brings any line holding more than the shop can fulfil down to what it can.
  //
  // Done on open rather than by blocking checkout: the shopper is looking at the bag here, so
  // the correction lands where they can see it and the checkout path stays untouched. Liquid
  // marks the rows and names the figure (see over_stock_attr in snippet 'cart-line-item') —
  // /cart.js carries no inventory field, so there is nowhere else to read it from.
  //
  // Routed through _handleQtyChange, so this is the same debounced, keyed change.js write the
  // stepper makes: Shopify still validates it and clamps further if the figure has moved since
  // the render, and _reportClampedLines still says "Only N left in stock" on the line. Nothing
  // here decides the quantity on its own — it only ever asks for less.
  _healOverStockLines() {
    for (const row of this.querySelectorAll('[data-item-key][data-line-over-stock]')) {
      const { itemKey: key, lineOverStock } = row.dataset;
      const available = parseInt(lineOverStock, 10);
      // 0 would remove the line, which is not a heal. Liquid only marks lines whose variant is
      // still purchasable, so this should never be under 1 — but do not act on it if it is.
      if (!Number.isFinite(available) || available < 1) continue;
      // Leave a line the shopper is already editing alone; their tap wins over this.
      if (this._pendingQty.has(key)) continue;
      // Recorded before the write: a heal asks for exactly what is available, so the server
      // grants it in full and the requested-vs-granted test below it never trips. Without this
      // the quantity would drop with nothing said, which reads as the cart eating an item.
      this._healedQty.set(key, available);
      this._handleQtyChange(key, available);
    }
  }

  async _stampSourceChannel() {
    const { state } = CartStore;
    if (!state || CartStore.pendingOps > 0) return;

    const stamped = await CartAPI.ensureSourceChannel(state);
    if (stamped !== state && CartStore.pendingOps === 0) CartStore.setState(stamped);
  }

  open() {
    this.previousActiveElement = document.activeElement;
    this.hidden = false;
    this.isOpen = true;
    this._stampSourceChannel();
    this._healOverStockLines();

    // The inline coupon row is part of this drawer but its data comes from the coupon API, so
    // make sure it has been fetched. Not `force: true`: this used to refetch on every bag open
    // to re-quote savings against a moved cart total, but that figure is computed locally by
    // coupon-drawer's _estimatedSaving and its CartStore subscriber already re-renders on any
    // cart change — so the round trip could only ever return the same data. load() is a no-op
    // once the page has it.
    document.querySelector('coupon-drawer')?.load();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.overlay?.classList.remove('opacity-0');
        this.overlay?.classList.add('opacity-100');
        this.panel?.classList.remove('translate-x-full');
        this.panel?.classList.add('translate-x-0');
        document.body.classList.add('overflow-hidden');
        this.setAttribute('aria-hidden', 'false');
        this.trapFocus();
      });
    });
  }

  close() {
    this.isOpen = false;
    this.removeTrapFocus();
    this.overlay?.classList.remove('opacity-100');
    this.overlay?.classList.add('opacity-0');
    this.panel?.classList.remove('translate-x-0');
    this.panel?.classList.add('translate-x-full');
    document.body.classList.remove('overflow-hidden');
    this.setAttribute('aria-hidden', 'true');

    setTimeout(() => {
      this.hidden = true;
      this.previousActiveElement?.focus({ preventScroll: true });
    }, TRANSITION_DURATION);
  }

  trapFocus() {
    const focusable = [...this.panel.querySelectorAll(FOCUSABLE_SELECTORS)];
    if (!focusable.length) return;

    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];

    // preventScroll: the panel may still be parked off-viewport, and focusing
    // into it would otherwise scroll the page behind the drawer.
    firstFocusable.focus({ preventScroll: true });
    this.removeTrapFocus();

    this.focusTrapHandler = (event) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey) {
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    };
    this.panel.addEventListener('keydown', this.focusTrapHandler);
  }

  removeTrapFocus() {
    if (this.focusTrapHandler && this.panel) {
      this.panel.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }

  _handleQtyRemove(event) {
    const minusBtn = event.target.closest('[data-quantity-minus]');
    const plusBtn = event.target.closest('[data-quantity-plus]');
    if (!minusBtn && !plusBtn) return;

    const btn = minusBtn ?? plusBtn;
    const row = btn.closest('[data-item-key]');
    if (!row) return;
    const key = row.dataset.itemKey;

    const input = row.querySelector('[data-cart-line-qty]');
    if (!input) return;

    // Count from the quantity already clicked to, not the one on screen. The rendered value only
    // moves when the server answers, so during the debounce window it still reads as it did
    // before the first tap — counting from it would make five taps land on 2 rather than 6.
    const renderedQty = parseInt(input.value, 10);
    const currentQty = this._pendingQty.get(key) ?? renderedQty;
    if (!Number.isFinite(currentQty)) return;

    if (minusBtn && currentQty === 1) {
      this._handleRemove(key);
      return;
    }

    const delta = plusBtn ? 1 : -1;
    const newQty = currentQty + delta;
    if (newQty < 1 || newQty > 99) return;

    // No stock ceiling is checked here. It used to be, from an inventory figure rendered into
    // the page — which is a snapshot, and a snapshot goes stale: restock a line and the cart
    // went on refusing to raise its quantity, quoting a limit that no longer existed. Shopify
    // clamps the write to what is actually available and says so in the response, so the flush
    // reports the real limit instead of predicting it (see _reportClampedLines).
    this._handleQtyChange(key, newQty);
  }

  // Currently-applied (applicable) discount codes, from the freshest cart state.
  _currentCodes() {
    return (CartStore.state?.discount_codes ?? [])
      .filter((d) => d.applicable && d.code)
      .map((d) => d.code);
  }

  // Public entry points for the coupon drawer. Routing through these keeps the
  // full-set resend, the Capillary sync fallback and the GoKwik-safe morph in
  // one place, rather than having a second component talk to the cart API.
  applyCoupon(code) {
    return this._enqueueCartWrite(() => this._addDiscount((code ?? '').trim()));
  }

  removeCoupon(code) {
    return this._enqueueCartWrite(() => this._removeDiscount((code ?? '').trim()));
  }

  // Stack a new code on top of the existing set (Shopify's `discount` field
  // replaces the whole set, so we must always resend the full list).
  _addDiscount(code) {
    if (!code) {
      // Applying with an empty field used to be a silent no-op.
      this._showCouponMsg('Please enter a coupon code');
      this.querySelector('[data-cart-coupon-input]')?.focus();
      return Promise.resolve({ ok: false, message: 'Please enter a coupon code' });
    }
    const current = this._currentCodes();
    const exists = current.some((c) => c.toLowerCase() === code.toLowerCase());
    const next = exists ? current : [...current, code];
    return this._submitDiscounts(next, { addedCode: code });
  }

  // Remove a single code by resending the set without it.
  _removeDiscount(code) {
    const next = this._currentCodes().filter((c) => c.toLowerCase() !== code.toLowerCase());
    return this._submitDiscounts(next, { removedCode: code });
  }

  // Feedback under the coupon field. A method rather than a local helper so the
  // empty-field validation in _addDiscount can reuse it.
  _showCouponMsg(text, ok = false) {
    const msg = this.querySelector('[data-cart-coupon-msg]');
    if (!msg) return;
    clearTimeout(this._couponMsgTimer);
    msg.textContent = text;
    msg.hidden = false;
    msg.classList.toggle('text-error', !ok);
    msg.classList.toggle('text-success', ok);
    this._couponMsgTimer = setTimeout(() => { msg.hidden = true; }, 2500);
  }

  async _submitDiscounts(codes, { addedCode = null, removedCode = null } = {}) {
    const sectionId = this.dataset.sectionId;
    const sections = sectionId ? [sectionId] : [];
    const show = (text, ok) => this._showCouponMsg(text, ok);

    // Loading state on the Apply button (only when applying, not when removing).
    const submitBtn = this.querySelector('[data-cart-coupon-submit]');
    const spinner = submitBtn?.querySelector('[data-cart-coupon-spinner]');
    const textEl = submitBtn?.querySelector('[data-cart-coupon-text]');
    const setLoading = (on) => {
      if (!addedCode || !submitBtn) return;
      submitBtn.disabled = on;
      if (spinner) spinner.hidden = !on;
      if (textEl) textEl.hidden = on;
    };

    setLoading(true);
    try {
      const data = await CartAPI.update({ discount: codes.join(',') }, sections);
      // Render before publishing to the store: setState notifies synchronously, and the coupon
      // drawer repaints its cards from that notification — it has to be painting into the
      // markup this render just put in place.
      // Held back if the shopper has tapped a stepper since this write left: the rows in this
      // response predate that tap, so drawing them would put the old number back under their
      // finger. The flush those taps are waiting on redraws with the discount a moment later.
      if (!this._hasNewerEdits() && sectionId && data.sections?.[sectionId]) {
        this.renderSection(data.sections[sectionId]);
      }
      CartStore.setState(data);

      const applied = data.discount_codes ?? [];
      const input = this.querySelector('[data-cart-coupon-input]');

      if (removedCode) {
        const message = `Coupon "${removedCode}" removed.`;
        show(message, true);
        return { ok: true, message };
      }

      if (addedCode) {
        let ok = applied.some(
          (d) => d.code?.toLowerCase() === addedCode.toLowerCase() && d.applicable
        );

        // Capillary fallback: if invalid, sync the coupon into Shopify then retry once.
        if (!ok && window.CapillaryCoupon?.isEnabled) {
          const result = await window.CapillaryCoupon.sync(addedCode);
          if (result.success || result.alreadyExists) {
            const retry = await CartAPI.update({ discount: codes.join(',') }, sections);
            if (!this._hasNewerEdits() && sectionId && retry.sections?.[sectionId]) {
              this.renderSection(retry.sections[sectionId]);
            }
            CartStore.setState(retry);
            const retryApplied = retry.discount_codes ?? [];
            ok = retryApplied.some(
              (d) => d.code?.toLowerCase() === addedCode.toLowerCase() && d.applicable
            );
          }
        }

        if (ok) {
          const message = `Coupon "${addedCode}" applied.`;
          show(message, true);
          if (input) input.value = '';
          return { ok: true, message };
        }

        const message = 'This coupon code isn’t valid.';
        show(message, false);
        return { ok: false, message };
      }

      return { ok: true, message: null };
    } catch (error) {
      const message = error?.description || error?.message || 'Could not apply coupon.';
      show(message, false);
      return { ok: false, message };
    } finally {
      setLoading(false);
    }
  }

  // Records the quantity the shopper has clicked to and lets the debounce send it.
  _handleQtyChange(key, newQty) {
    if (!CartStore.state) return;

    // The shopper has moved this line on, so a clamp recorded against the quantity they were at
    // is no longer what they are looking at. Their own action clears it; nothing else does.
    this._clearLineError(key);

    // Booked on the click, not when the write leaves, so checkout is gated for the whole time
    // the edit is outstanding — the debounce window included. Booking it at flush time left a
    // 250ms gap in which a shopper could reach checkout and lose the change they just made.
    // One op per line, however many times that line is tapped; _executeMutation closes out
    // snapshot.size of them together.
    if (!this._pendingQty.has(key)) CartStore.beginOp();

    this._pendingQty.set(key, newQty);
    this._echoQty(key, newQty);
    this._scheduleFlush();
  }

  // Shows the number just pressed, and marks the figures derived from it as still settling.
  //
  // This echoes the click rather than predicting the cart. The quantity is not a guess — it is
  // exactly what the shopper pressed — while every money figure stays whatever the server last
  // rendered until it sends a new one, so nothing here can put a price on screen that the cart
  // disagrees with. Without it the stepper sat inert for the debounce plus the round trip, and
  // read as not having registered the tap at all.
  _echoQty(key, newQty) {
    const row = this.querySelector(`[data-item-key="${key}"]`);
    if (!row) return;

    const input = row.querySelector('[data-cart-line-qty]');
    if (input) input.value = newQty;

    // At 1 the minus button acts as remove, so its icon has to follow the echoed value —
    // otherwise it shows a minus while the next click would delete the line.
    const minusBtn = row.querySelector('[data-quantity-minus]');
    if (minusBtn) {
      const deleteIcon = minusBtn.querySelector('[data-icon-delete]');
      const minusIcon = minusBtn.querySelector('[data-icon-minus]');
      if (deleteIcon) deleteIcon.hidden = newQty !== 1;
      if (minusIcon) minusIcon.hidden = newQty === 1;
    }

    // Prices are the server's to state. Marking them pending says "this figure is about to
    // change" without saying what to. The class needs no cleanup: the render that follows
    // replaces these nodes outright, and so does the resync if the write fails.
    const priceEls = row.querySelectorAll('[data-cart-line-price], [data-cart-line-compare-price]');
    for (const el of priceEls) el.classList.add('animate-pulse');
  }

  // Redraws the drawer from the server's own markup for this section. Every cart write asks
  // for `sections: [cart_drawer]`, so the HTML arrives in the write's response and this costs
  // no extra request.
  //
  // Region by region rather than replacing the whole drawer, for two reasons. The panel and
  // overlay carry the open/close transition classes, so they have to survive. And the footer
  // holds GoKwik's injected checkout button: only [data-cart-totals] inside it is replaced,
  // leaving their node — and the .is-cart-busy state on it — untouched.
  //
  // Liquid decides which regions show, via the `hidden` attribute it renders from
  // cart.item_count; CartRender.swap carries that across so empty-versus-filled is not
  // recomputed here.
  // Returns whether anything was actually drawn, so a caller that has to leave the drawer in a
  // correct state (product-form.js's failed-add recovery) can tell a real render from a miss.
  renderSection(sectionHtml) {
    const fresh = CartRender.parse(sectionHtml, 'cart-drawer');
    if (!fresh) return false;

    CartRender.swap(fresh, this, '[data-cart-header-count]');
    CartRender.swap(fresh, this, '[data-cart-notice]');
    CartRender.swap(fresh, this, '[data-cart-items]');
    CartRender.swap(fresh, this, '[data-cart-extras]');
    CartRender.swap(fresh, this, '[data-cart-empty]');
    CartRender.swap(fresh, this, '[data-cart-totals]');
    CartRender.mirrorHidden(fresh, this, '[data-cart-footer]');

    // The root itself is never swapped, so carry its stamp across by hand — otherwise the
    // drawer would still be claiming the count it was first rendered with.
    if (fresh.dataset.renderedCount !== undefined) {
      this.dataset.renderedCount = fresh.dataset.renderedCount;
    }

    // The rows this reads have just been replaced, so the checkout controls have to be re-judged
    // against them — this is what closes checkout the moment a line renders as unavailable.
    this._syncCheckoutAvailability();

    // The trap closes over the first and last focusable elements it found, and a redraw can
    // add or remove both (a removed line, an appearing empty state). Re-read them so Tab
    // still cycles inside the drawer.
    if (this.isOpen) this.trapFocus();

    // Delivery dates are written into the line rows, which have just been replaced. The
    // element re-connects along with [data-cart-extras] and fills itself, but only because
    // items happen to be swapped first — asking it directly keeps the fill correct whatever
    // order these regions are swapped in.
    this.querySelector('cart-serviceability')?.refresh();

    // Last, because the swap above replaced the rows these are written into. This is what makes
    // a clamp survive a render it did not belong to — the discount write and the resync paths
    // both land here, and neither knows anything about stock.
    this._paintClampNotices();

    return true;
  }

  // Redraws from a fresh fetch, for the paths with no write response to read markup out of: a
  // back/forward restore, and recovering after a write failed part-way.
  //
  // Fetched from the current path rather than the site root, so that CartRender's fallback to
  // the whole document is a page this drawer is known to render on. The root is the gender
  // splash, which need not carry a cart drawer for the fallback to find.
  async renderFromServer() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return false;

    const html = await CartRender.fetchSection(sectionId, window.location.pathname);
    return html ? this.renderSection(html) : false;
  }

  // Whether any quantity edit is newer than the response a caller is about to render — either
  // flushed and waiting its turn in the chain, or still sitting in the debounce window.
  //
  // _queuedMutations on its own does not answer that. It is decremented as the chain link
  // starts, so it reads 0 for the whole time a request is in flight, and a tap landing in that
  // window only records itself in _pendingQty and arms the debounce — it never reaches the
  // counter. Both halves are needed, and the timer is checked too so the answer holds even if
  // a future caller drains the map without disarming it.
  _hasNewerEdits() {
    return this._queuedMutations > 0 || this._pendingQty.size > 0 || this._flushTimer !== null;
  }

  // Re-resolves a line key that may have gone stale while an edit sat in the debounce.
  //
  // A key is `<variant_id>:<hash>`, and Shopify recomputes that hash whenever the cart's
  // discounts change. So applying or removing a coupon silently re-keys every line, and the key
  // captured when the shopper tapped is dead by the time the debounce sends it — change.js
  // answers 400 `no valid id or line parameter`, the catch resyncs from the server, and the
  // stepper snaps back to the quantity the shopper had just moved off. That is the whole of the
  // "qty resets with no error message" report: the write is rejected, not raced.
  //
  // The variant id in front of the colon does not move, and this theme sets no line item
  // properties, so a variant appears at most once in the cart — which makes the prefix enough
  // to find the line again. Matched exactly first all the same, so a cart that ever does carry
  // properties degrades to today's behaviour rather than picking the wrong line.
  _resolveKey(key) {
    return this._findLine(CartStore.state?.items ?? [], key)?.key ?? key;
  }

  // The line `key` refers to within `items`, or null if that variant is not there at all.
  //
  // Exact match first, so a cart that ever does carry line item properties degrades to matching
  // by key alone rather than picking the wrong line. Shared by _resolveKey, which asks about the
  // cart the store holds, and _reportClampedLines, which asks about the cart a write returned.
  _findLine(items, key) {
    const exact = items.find((item) => item.key === key);
    if (exact) return exact;

    const variantId = String(key).split(':')[0];
    const matches = items.filter((item) => String(item.key).split(':')[0] === variantId);
    return matches.length === 1 ? matches[0] : null;
  }

  // Puts a cart write that is not a quantity edit — currently the discount updates — on the
  // same chain the stepper and the remove button use, so the two can never overlap.
  //
  // Ordering is what makes _resolveKey trustworthy: it resolves against the cart CartStore
  // holds, which is only as fresh as the last response to land. A discount write running
  // alongside an in-flight change.js could still re-key the line underneath it, and no amount
  // of re-resolving would catch that. Serialised, the resolve always runs after the write that
  // moved the keys has settled.
  //
  // Edits still sitting in the debounce are sent now rather than waited out — the shopper has
  // moved on to the coupon, so their intent for the stepper is settled.
  _enqueueCartWrite(task) {
    clearTimeout(this._flushTimer);
    this._flushTimer = null;
    this._flush();

    const result = this._mutationChain.then(() => task());
    // The chain must stay resolved for whatever links after it; `task` reports its own failure
    // to the shopper, but a future caller's need not.
    this._mutationChain = result.then(() => {}, () => {});
    return result;
  }

  // Holds a run of taps back briefly so five clicks on one line become one write rather than
  // five. The number on screen follows each tap (see _echoQty); only the request waits.
  _scheduleFlush() {
    clearTimeout(this._flushTimer);
    this._flushTimer = setTimeout(() => this._flush(), 250);
  }

  _flush() {
    this._flushTimer = null;
    if (this._pendingQty.size === 0) return;

    const snapshot = new Map(this._pendingQty); // key → quantity
    this._pendingQty.clear();

    this._queuedMutations++;
    this._mutationChain = this._mutationChain.then(() => {
      this._queuedMutations--;
      return this._executeMutation(snapshot);
    });
  }

  // Tells the shopper when the server gave them less than they asked for.
  //
  // This is what replaced the stock ceiling the cart used to hold. Shopify clamps a quantity
  // change to the stock it can honour and answers 200, so there is no error to catch — the only
  // evidence is the quantity that comes back. Reading it from the response means the limit
  // quoted is always the live one, where a figure rendered into the page went stale the moment
  // inventory moved and started refusing increases that were perfectly valid.
  _reportClampedLines(snapshot, cartData) {
    const items = cartData?.items ?? [];

    for (const [key, requested] of snapshot) {
      // Not a plain key match. A key is `<variant_id>:<hash>` and Shopify re-hashes it whenever
      // the line's discount allocations change — which is exactly what raising a quantity past
      // an order-level minimum spend does. The response then describes the same line under a
      // NEW key, this lookup missed, and the clamp was filed as "the line is gone" and never
      // stated. That is the whole of "it reports when the bag is already at the limit but not
      // when the quantity climbs to it": at the limit nothing moves, so no threshold is crossed
      // and no key changes.
      const line = this._findLine(items, key);
      // Still a removal if the variant has genuinely left the cart.
      if (!line) continue;
      // A heal is the one case where the cart, not the shopper, picked the number — and it picks
      // exactly what is available, so the server grants it in full and the test below finds
      // nothing to report. It is therefore also the one case that must always speak.
      const wasHealed = this._healedQty.delete(key);
      if (!wasHealed && line.quantity >= requested) continue;
      // On the line, not in the notice band at the top of the drawer: the limit belongs to one
      // product, and it reads next to the quantity that just changed rather than as a banner
      // about the whole bag.
      // Filed under the key the response uses, not the one the shopper tapped: the render that
      // follows draws the row with the new key, and a notice held under the dead one would have
      // no row to paint into.
      this._noteClamp(line.key, CartRender.clampedMessage(line.quantity));
    }
  }

  async _executeMutation(snapshot) {
    const sectionId = this.dataset.sectionId;
    const sections = sectionId ? [sectionId] : [];

    // One key-based change.js per edited line, in sequence.
    //
    // Not update.js, which would send the whole batch in a single request: it "doesn't validate
    // the quantity on variants that are already in the cart", so a shopper could set any
    // quantity they liked and the cart would carry a total it cannot fulfil all the way to
    // payment. change.js clamps to the stock actually available and answers with the quantity it
    // granted, which is the only reason _reportClampedLines has anything to report and the only
    // reason this cart no longer needs an inventory figure of its own.
    //
    // Not a positional `updates` array either: it has to match the server's line count exactly
    // and Shopify rejects any drift wholesale with "One or more items have changed" — an error
    // the shopper sees and cannot act on. A key is unambiguous, so nothing has to be kept in
    // step. The cart page avoids it the same way (changeByKey in cart.js).
    //
    // The debounce means this is one request per *line* edited, which for a stepper is almost
    // always exactly one however many times it was tapped.

    // Re-resolved here, not at flush time: this runs only once the writes queued ahead of it
    // have settled, so CartStore holds the cart these keys have to match. If one of those was a
    // discount write it re-keyed every line, and the keys in `snapshot` — captured when the
    // shopper tapped — are already dead. `live` is what actually goes to the server, and what
    // the clamp and error reporting below address rows by.
    const live = new Map();
    for (const [key, quantity] of snapshot) {
      const liveKey = this._resolveKey(key);
      if (liveKey !== key && this._healedQty.has(key)) {
        this._healedQty.set(liveKey, this._healedQty.get(key));
        this._healedQty.delete(key);
      }
      live.set(liveKey, quantity);
    }

    let data = null;

    this._executingMutations++;
    try {
      for (const [key, quantity] of live) {
        data = await CartAPI.change(key, quantity, sections);
      }

      // Only the last write in a run redraws. An earlier response describes fewer changes than
      // the shopper has already made, so rendering it would show the cart mid-way and then
      // correct itself.
      //
      // This used to test _queuedMutations === 0, which is true throughout the request (see
      // _hasNewerEdits), so a tap arriving while it was in flight did not protect anything:
      // the stale response redrew the rows and snapped the number off the echoed value until
      // the next flush put it back. Holding + made that fire once per request, which is the
      // "quantity jumps around and settles a moment later" report.
      // Recorded before the render and outside the staleness gate. This response is the only
      // place a clamp is ever visible — change.js grants less and answers 200 with no message —
      // so skipping the read because the shopper had already tapped again discarded the evidence
      // for good, and the line went quiet about a limit it had just hit. Recording is now
      // separate from painting: _noteClamp keeps the message and every render repaints it, so it
      // no longer has to be this response's turn to draw for the limit to be stated.
      this._reportClampedLines(live, data);

      if (!this._hasNewerEdits() && sectionId && data?.sections?.[sectionId]) {
        this.renderSection(data.sections[sectionId]);
      }

      // Nothing derived from the cart may move until the shopper stops. A response that is
      // already behind them describes a cart they have moved on from, and adopting it walks the
      // bag badge, the coupon saving and the totals through intermediate states while the
      // stepper sits at the number actually pressed.
      //
      // Passing null settles the op accounting — pendingOps, the pulse, the checkout gate —
      // WITHOUT adopting the state, so reconcile does not run and no subscriber has anything
      // new to paint. The state is not lost: the last write in the run carries the truth, and a
      // failure anywhere in the run refetches from the server in the catch below.
      CartStore.settle(this._hasNewerEdits() ? null : data, snapshot.size);
    } catch (error) {
      CartStore.failOp(snapshot.size);

      // A failure part-way through the loop leaves the earlier lines committed on the server
      // and the later ones not, and this response carries no markup to redraw from. Refetch
      // rather than reason about which landed: the server's own copy is right by definition.
      await CartStore.refresh();
      await this.renderFromServer();

      // After the redraw, not before — it replaces the rows these errors are written into.
      for (const key of live.keys()) {
        // The resync above re-read the cart, so whatever clamp was recorded for this line
        // describes a write that never landed. The failure is the thing to state.
        this._clampNotices.delete(key);
        this._showLineError(key, error?.description || error?.message || null);
      }
    } finally {
      this._executingMutations--;
    }
  }

  _handleRemove(key) {
    if (!CartStore.state) return;

    // The line is going; any clamp owed to it goes too, so a later render cannot resurrect a
    // stock message about a product no longer in the bag.
    this._clearLineError(key);

    const row = this.querySelector(`[data-item-key="${key}"]`);
    if (!row) return;

    // Drop any quantity edits still waiting on the debounce. Sending them would be writing to a
    // line that is about to stop existing. Each carries an op booked on its first click, and
    // clearing the timer means no flush will ever close them out — so release them here or
    // checkout stays gated for the rest of the session.
    clearTimeout(this._flushTimer);
    this._flushTimer = null;
    if (this._pendingQty.size > 0) CartStore.failOp(this._pendingQty.size);
    this._pendingQty.clear();

    const sectionId = this.dataset.sectionId;
    const sections = sectionId ? [sectionId] : [];

    // The row stays put until the server confirms. Dimming the region says the removal is
    // being worked on and stops a second click landing on a line already on its way out.
    this.panel?.classList.add('cart-loading');

    CartStore.beginOp();
    this._queuedMutations++;
    this._mutationChain = this._mutationChain.then(async () => {
      this._queuedMutations--;
      this._executingMutations++;
      // Resolved inside the chain link for the same reason as a quantity write: a discount
      // update queued ahead of this one will have re-keyed the line since the click.
      const liveKey = this._resolveKey(key);
      try {
        // By key, not by index: with several removals queued the index of this line shifts as
        // the earlier ones land, and resolving it here used to depend on every response having
        // already rewritten the store.
        const data = await CartAPI.change(liveKey, 0, sections);
        if (!this._hasNewerEdits() && sectionId && data.sections?.[sectionId]) {
          this.renderSection(data.sections[sectionId]);
        }
        // Same rule as the quantity path: if the shopper has already moved on, settle the op
        // without adopting the state, so the markup this render just skipped and the figures
        // driven off the store cannot drift apart.
        CartStore.settle(this._hasNewerEdits() ? null : data);
      } catch (error) {
        console.error('[CartDrawer] remove failed:', error);
        CartStore.failOp();

        // Resync for the same reason as a failed quantity write, and for one more: this handler
        // discards the quantity edits that were waiting on the debounce, and those lines are
        // still showing the number the shopper clicked to. Nothing else would correct them.
        await CartStore.refresh();
        await this.renderFromServer();

        // After the redraw — it replaces the row this is written into.
        this._showLineError(liveKey, error?.description || error?.message || null);
      } finally {
        this._executingMutations--;
        if (this._queuedMutations + this._executingMutations === 0) {
          this.panel?.classList.remove('cart-loading');
        }
      }
    });
  }

  showError(message) {
    const errEl = this.querySelector('[data-cart-error]');
    if (!errEl) return;
    errEl.textContent = message;
    errEl.hidden = false;
    setTimeout(() => { errEl.hidden = true; }, 4000);
  }

  // A correction the shopper should read, not a failure — see CartRender.showNotice for why
  // it does not clear itself the way showError does.
  showNotice(message) {
    CartRender.showNotice(this, message);
  }

  // Whether the bag holds a line Liquid has marked unfulfillable. /cart.js carries no
  // availability field, so the rendered markup is the only place this is known — see
  // cart-line-item.liquid.
  _hasUnavailableLines() {
    return this.querySelector('[data-item-key][data-line-unavailable]') !== null;
  }

  // Serviceability closes checkout for two further reasons, both stamped by
  // <cart-serviceability>: a line the API authoritatively answered `serviceable:
  // false` for, and nothing else. An error, an unknown SKU or a
  // request that never resolved stamp neither, so a backend outage cannot close
  // checkout for the whole store — it only ever stops a known-undeliverable order.
  _isCheckoutBlocked() {
    return (
      this._hasUnavailableLines() ||
      this.querySelector('[data-item-key][data-line-unserviceable]') !== null
    );
  }

  // Closes checkout off while such a line is present. Shopify would reject the order at payment
  // anyway, and this is the only screen where the shopper can put it right — the notice band
  // above the items is what tells them to remove it or change the quantity.
  //
  // The theme's own button takes `disabled`. GoKwik's cannot: their observer strips `disabled`
  // and `.disabled` roughly every 700ms, so theirs is held by a class of ours that they never
  // touch, exactly as .is-cart-busy already does for a cart that is still settling.
  _syncCheckoutAvailability() {
    const blocked = this._isCheckoutBlocked();

    const checkoutBtn = this.querySelector('[data-cart-checkout]');
    if (checkoutBtn) {
      checkoutBtn.disabled = blocked;
      checkoutBtn.toggleAttribute('aria-disabled', blocked);
    }

    this.querySelector('.gokwik-checkout')?.classList.toggle('is-cart-blocked', blocked);
  }

  // Records a clamp against a line and states it now if there are rows to state it in.
  _noteClamp(key, message) {
    if (!message) return;
    this._clampNotices.set(key, message);
    this._paintClampNotices();
  }

  // The variant half of a line key. A key is `<variant_id>:<hash>` and Shopify re-hashes it
  // whenever the line's discount allocations change, so one line can be known by several keys
  // inside a single tapped run. The variant is the part that holds still, which is why nothing
  // below matches on the whole key alone.
  _variantOf(key) {
    return String(key).split(':')[0];
  }

  // The row a message belongs to, found by key and then by variant.
  //
  // Resolved against the DOM rather than against CartStore: mid-write the store still holds the
  // cart as it was BEFORE the response being reported on, so resolving there mapped a notice
  // recorded under the line's new key back onto its dead one — and the render that followed drew
  // the row under the new key, leaving nothing to paint into. The row is where the message has
  // to land, so the row is what decides which key it is filed under.
  _findRow(key) {
    const exact = this.querySelector(`[data-item-key="${key}"]`);
    if (exact) return exact;

    // Only when the variant is unambiguous in the bag, the same guard _findLine applies: a cart
    // that ever does carry line item properties can hold one variant on several lines, and
    // writing a stock message onto the wrong one is worse than not writing it at all.
    const rows = this.querySelectorAll(`[data-item-key^="${this._variantOf(key)}:"]`);
    return rows.length === 1 ? rows[0] : null;
  }

  // Writes every clamp still owed into its line, re-filing it under whatever key that line is
  // currently drawn with.
  _paintClampNotices() {
    // Snapshot: re-filing writes back into the map, and a Map iterator would then visit the key
    // it has just added.
    for (const [key, message] of [...this._clampNotices]) {
      const row = this._findRow(key);
      // No row for it yet: the render that will carry this line has not happened. The record
      // stays, and the paint at the end of renderSection lands it.
      if (!row) continue;

      const liveKey = row.dataset.itemKey;
      if (liveKey !== key) {
        this._clampNotices.delete(key);
        clearTimeout(this._lineErrorTimers.get(key));
        this._lineErrorTimers.delete(key);
        this._clampNotices.set(liveKey, message);
      }

      // Sticky: a clamp is a correction, not a failure. CartRender.showNotice makes the same
      // call for the band above the items — "the cart it describes has changed, so it stays
      // until the shopper dismisses it". A timed message here was another way for this to read
      // as random: it appears only once the last write of a tapped run lands, which is already a
      // second or two after the shopper stopped, and then takes itself away while they are still
      // looking at the number that moved.
      this._showLineError(liveKey, message, { sticky: true });
    }
  }

  // Forgets a line's message: the record, its timer, and what is on screen. All three, because
  // dropping the record alone left the old text sitting under the shopper's finger until some
  // later render happened to clear it — and the message describes a quantity they have just
  // moved off.
  //
  // Matched by variant, so a notice filed under a key this line has since been re-hashed out of
  // is forgotten too rather than surfacing again at the next paint.
  _clearLineError(key) {
    const variant = this._variantOf(key);
    for (const noticeKey of [...this._clampNotices.keys()]) {
      if (this._variantOf(noticeKey) !== variant) continue;
      this._clampNotices.delete(noticeKey);
      clearTimeout(this._lineErrorTimers.get(noticeKey));
      this._lineErrorTimers.delete(noticeKey);
    }
    clearTimeout(this._lineErrorTimers.get(key));
    this._lineErrorTimers.delete(key);

    const errEl = this._findRow(key)?.querySelector('[data-cart-line-error]');
    if (errEl) errEl.hidden = true;
  }

  // `sticky` leaves the message up until the shopper acts on the line. Used for clamps; a
  // failure stays timed, because it describes a write that did not happen rather than a cart
  // that has changed under them.
  _showLineError(key, message = null, { sticky = false } = {}) {
    const errEl = this._findRow(key)?.querySelector('[data-cart-line-error]');
    if (!errEl) return;
    errEl.textContent = message ?? errEl.dataset.errorMessage;
    errEl.hidden = false;

    // One timer per line, replaced rather than stacked. Two messages on the same line used to
    // share the first one's deadline, so the second vanished early; and a timer armed before a
    // render went on holding the row that render had already thrown away. Cleared even in the
    // sticky case, so a clamp landing on top of an earlier failure is not hidden by its timer.
    clearTimeout(this._lineErrorTimers.get(key));
    this._lineErrorTimers.delete(key);
    if (sticky) return;

    this._lineErrorTimers.set(
      key,
      setTimeout(() => {
        errEl.hidden = true;
        this._lineErrorTimers.delete(key);
      }, LINE_ERROR_DURATION),
    );
  }

  // Replay the click that was blocked while the cart settled. GoKwik owns its
  // own click handler, so the button is re-clicked rather than navigated to a
  // URL — _checkoutUrl is empty when their button replaces the theme's.
  _resumeCheckout() {
    const gokwikBtn = this.querySelector('.gokwik-checkout button');
    if (gokwikBtn) {
      gokwikBtn.click();
      return;
    }
    if (this._checkoutUrl) window.location.href = this._checkoutUrl;
  }

  _onStoreChange(state, pendingOps) {
    if (!state) return;

    const checkoutBtn = this.querySelector('[data-cart-checkout]');
    const spinnerEl = this.querySelector('[data-checkout-spinner]');
    const checkoutTextEl = this.querySelector('[data-checkout-text]');

    // Only the busy state is cleared here. Whether the button may be pressed at all is
    // _syncCheckoutAvailability's call, below — settling used to hand `disabled = false` back
    // unconditionally, which would reopen a checkout the bag is meant to be keeping shut.
    if (checkoutBtn && pendingOps === 0) {
      checkoutBtn.removeAttribute('aria-busy');
      if (spinnerEl) spinnerEl.hidden = true;
      if (checkoutTextEl) checkoutTextEl.hidden = false;
    }

    // Resume a checkout the shopper asked for while the cart was still settling.
    // Kept outside the branch above because that button does not exist when
    // GoKwik is enabled.
    if (pendingOps === 0) {
      this.querySelector('.gokwik-checkout')?.classList.remove('is-cart-busy');

      if (this._checkoutPending) {
        this._checkoutPending = false;
        // A cart can settle into a state it cannot check out from — stock dropping away
        // mid-write — so the resume is not unconditional.
        if (!this._hasUnavailableLines()) this._resumeCheckout();
      }

      // Only once the cart is settled — mid-write the store is meant to be ahead of the
      // markup, and that is not the staleness this is looking for.
      this._reconcileStaleMarkup(state.item_count);
    }

    this._syncCheckoutAvailability();

    // Which regions show — items versus the empty state, whether the footer and extras are
    // there at all — is decided by the `hidden` attributes Liquid renders from cart.item_count
    // and carried across by renderSection. It used to be recomputed here, from a cart that
    // could be running ahead of the server, and the two could disagree.
    const isEmpty = state.item_count === 0;

    // The count pill inside the drawer. renderSection covers it after a write; this covers the
    // paths that move the cart without re-rendering the section, such as the store initialising
    // or the source_channel heal. The bubbles out in the header are CartStore.reconcile's.
    const headerCount = this.querySelector('[data-cart-header-count]');
    if (headerCount) {
      headerCount.textContent = state.item_count > 0 ? state.item_count : '';
      headerCount.hidden = isEmpty;
    }

    // Neither the bag count nor the subtotal leads the server any more, so these are the
    // standing signal that a figure on screen is about to change. Driven off pendingOps, which
    // is booked on the click, so the pulse starts under the shopper's finger rather than when
    // the request finally leaves.
    for (const el of [headerCount, this.querySelector('[data-cart-total]')]) {
      el?.classList.toggle('animate-pulse', pendingOps > 0);
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
